import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Rating,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import { businessService } from '../services/businessService';
import { getBookingPath } from '../constants/routes';
import { formatDuration } from '../utils/dateUtils';

/**
 * Business detail page
 */
const BusinessDetailPage = () => {
  const { businessId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { customerIds, isBusinessOwner } = useAuth();
  
  // Get customerId from state, or use first customerId if business owner and no businessId in URL
  const customerId = location.state?.customerId || 
    (isBusinessOwner && customerIds && customerIds.length > 0 ? customerIds[0] : null);
  
  // If business owner viewing their own business and no businessId, use customerId as businessId
  const effectiveBusinessId = businessId || (isBusinessOwner ? customerId : null);
  
  const [business, setBusiness] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBusinessDetails = async () => {
      console.log('BusinessDetailPage Debug:', {
        businessId,
        customerId,
        effectiveBusinessId,
        isBusinessOwner,
        customerIds,
      });

      if (!customerId || !effectiveBusinessId) {
        setError('Business not found');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [bizData, calData] = await Promise.all([
          businessService.getBusinessById(customerId, effectiveBusinessId),
          businessService.getCalendars(customerId),
        ]);
        setBusiness(bizData);
        setCalendars(calData);
      } catch (err) {
        console.error('Error fetching business details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessDetails();
  }, [customerId, effectiveBusinessId]);

  const handleBookNow = (calendarId = null) => {
    navigate(getBookingPath(effectiveBusinessId), {
      state: { 
        customerId, 
        calendarId,
        business,
      },
    });
  };

  if (loading) {
    return <LoadingSpinner message={t('common.loading')} />;
  }

  if (error || !business) {
    return <ErrorDisplay message={error || 'Business not found'} />;
  }

  // Get meeting types from calendars
  const allMeetingTypes = calendars.reduce((acc, cal) => {
    if (cal.meeting_types) {
      Object.entries(cal.meeting_types).forEach(([key, value]) => {
        if (!acc[key]) {
          acc[key] = { ...value, name: key, calendarId: cal.id };
        }
      });
    }
    return acc;
  }, {});

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Business Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 3, sm: 4 } }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box
                sx={{
                  width: { xs: 60, sm: 80 },
                  height: { xs: 60, sm: 80 },
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography 
                  variant="h3" 
                  color="white"
                  sx={{ fontSize: { xs: '2rem', sm: '3rem' } }}
                >
                  {business.business_name?.charAt(0)?.toUpperCase() || 'B'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, width: '100%' }}>
                <Typography 
                  variant="h4" 
                  component="h1"
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {business.business_name}
                </Typography>
                {business.category && (
                  <Chip label={business.category} size="small" sx={{ mt: 1 }} />
                )}
              </Box>
            </Box>

            {business.rating !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Rating value={business.rating} precision={0.5} readOnly size="small" />
                <Typography variant="body2" color="text.secondary">
                  ({business.rating?.toFixed(1)}) · {business.total_bookings || 0} bookings
                </Typography>
              </Box>
            )}

            <Typography 
              variant="body1" 
              paragraph
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              {business.description}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {t('business.contact')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {business.contact_info?.address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.5, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {business.contact_info.address}
                    </Typography>
                  </Box>
                )}
                {business.contact_info?.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {business.contact_info.phone}
                    </Typography>
                  </Box>
                )}
                {business.contact_info?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {business.contact_info.email}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                onClick={() => handleBookNow()}
              >
                {t('business.bookNow')}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Services / Meeting Types */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          {t('business.services')}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {Object.keys(allMeetingTypes).length === 0 ? (
          <Typography variant="body1" color="text.secondary">
            {t('business.noServices')}
          </Typography>
        ) : (
          <List>
            {Object.entries(allMeetingTypes).map(([key, type]) => (
              <ListItem 
                key={key} 
                sx={{ 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1, 
                  mb: 1 
                }}
              >
                <ListItemText
                  primary={type.description || key}
                  secondary={`${formatDuration(parseInt(type.duration))}`}
                />
                <ListItemSecondaryAction>
                  <Button
                    variant="outlined"
                    onClick={() => handleBookNow(type.calendarId)}
                  >
                    {t('business.bookNow')}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default BusinessDetailPage;
